import React, { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { FeatherX } from '../ui/icons';
import TaskDueDate from '../shared/TaskDueDate';
import TaskConversationViewer from '../Manager/TaskConversationViewer';
import FeatureTagDetailModal from '../FeatureTags/FeatureTagDetailModal';
import { managerApi } from '../../services/api';
import { isManager } from '../../utils/roleUtils';
import api from '../../services/api';
import './TaskDetailModal.css';

const TaskDetailModal = ({ task, isOpen, onClose }) => {
    const [userIsManager, setUserIsManager] = useState(false);
    const [showConversations, setShowConversations] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [loadingConversations, setLoadingConversations] = useState(false);
    const [isFeatureTagModalOpen, setIsFeatureTagModalOpen] = useState(false);

    useEffect(() => {
        if (isOpen && task?.id) {
            checkUserRole();
        }
    }, [isOpen, task?.id]);

    useEffect(() => {
        if (isOpen && task?.id && userIsManager) {
            loadConversations();
        }
    }, [isOpen, task?.id, userIsManager]);

    const checkUserRole = async () => {
        try {
            const response = await api.get('/api/auth/role');
            const userRole = response.data?.role;
            setUserIsManager(isManager(userRole));
        } catch (err) {
            console.error('Error checking user role:', err);
        }
    };

    const loadConversations = async () => {
        if (!task?.id) return;
        setLoadingConversations(true);
        try {
            const response = await managerApi.getTaskConversations(task.id);
            if (response.success && response.data) {
                setConversations(response.data);
            }
        } catch (error) {
            console.error('Error loading conversations:', error);
        } finally {
            setLoadingConversations(false);
        }
    };

    if (!isOpen || !task) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div 
            className="task-detail-modal-overlay"
            onClick={onClose}
        >
            <div 
                className="task-detail-modal"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="task-detail-modal-header">
                    <div className="task-detail-modal-title-section">
                        <h3 className="task-detail-modal-title">{task.title}</h3>
                        {task.display_id && (
                            <span className="task-detail-modal-id">{task.display_id}</span>
                        )}
                    </div>
                    <button 
                        className="task-detail-modal-close"
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        <FeatherX size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="task-detail-modal-content">
                    {/* Status and Basic Info */}
                    <div className="task-detail-modal-section">
                        <div className="task-detail-modal-info-row">
                            <div className="task-detail-modal-info-item">
                                <span className="task-detail-modal-label">Status:</span>
                                <Badge variant={
                                    task.status === 'completed' || task.status === 'released' ? 'success' :
                                    task.status === 'in-progress' ? 'warning' : 'secondary'
                                }>
                                    {task.status}
                                </Badge>
                            </div>
                            {task.feature_tag?.due_date && (
                                <div className="task-detail-modal-info-item">
                                    <span className="task-detail-modal-label">Due Date:</span>
                                    <TaskDueDate dueDate={task.feature_tag.due_date} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Feature Tag */}
                    {task.feature_tag && (
                        <div className="task-detail-modal-section">
                            <div className="task-detail-modal-info-item">
                                <span className="task-detail-modal-label">Feature Tag:</span>
                                <div 
                                    className="task-detail-modal-feature-tag clickable-feature-tag"
                                    onClick={() => setIsFeatureTagModalOpen(true)}
                                    title="Click to view feature details"
                                >
                                    <div 
                                        className="task-detail-modal-feature-tag-color"
                                        style={{ backgroundColor: task.feature_tag.color || '#6366f1' }}
                                    />
                                    <span>{task.feature_tag.name}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Description */}
                    {task.description && (
                        <div className="task-detail-modal-section">
                            <div className="task-detail-modal-info-item task-detail-modal-info-item-full">
                                <span className="task-detail-modal-label">Description:</span>
                                <p className="task-detail-modal-description">{task.description}</p>
                            </div>
                        </div>
                    )}

                    {/* Hours and Notes (for picked up tasks) */}
                    {(task.hours > 0 || task.notes) && (
                        <div className="task-detail-modal-section">
                            {task.hours > 0 && (
                                <div className="task-detail-modal-info-item">
                                    <span className="task-detail-modal-label">Hours Logged:</span>
                                    <span className="task-detail-modal-value">{task.hours}h</span>
                                </div>
                            )}
                            {task.notes && (
                                <div className="task-detail-modal-info-item task-detail-modal-info-item-full">
                                    <span className="task-detail-modal-label">Notes:</span>
                                    <p className="task-detail-modal-notes">{task.notes}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Days Since Assignment (for unpicked tasks) */}
                    {task.daysSinceAssignment !== undefined && (
                        <div className="task-detail-modal-section">
                            <div className="task-detail-modal-info-item">
                                <span className="task-detail-modal-label">Days Since Assignment:</span>
                                <span className="task-detail-modal-value">
                                    {task.daysSinceAssignment === 0 ? 'Today' : `${task.daysSinceAssignment} day${task.daysSinceAssignment !== 1 ? 's' : ''}`}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Metadata */}
                    <div className="task-detail-modal-section task-detail-modal-section-meta">
                        {task.created_at && (
                            <div className="task-detail-modal-info-item">
                                <span className="task-detail-modal-label">Created:</span>
                                <span className="task-detail-modal-value task-detail-modal-value-meta">
                                    {formatDate(task.created_at)}
                                </span>
                            </div>
                        )}
                        {task.updated_at && task.updated_at !== task.created_at && (
                            <div className="task-detail-modal-info-item">
                                <span className="task-detail-modal-label">Updated:</span>
                                <span className="task-detail-modal-value task-detail-modal-value-meta">
                                    {formatDate(task.updated_at)}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Conversations Section (Manager Only) */}
                    {userIsManager && (
                        <div className="task-detail-modal-section task-detail-modal-section-conversations">
                            <div className="task-detail-modal-conversations-header">
                                <h4 className="task-detail-modal-conversations-title">
                                    Manager Discussions
                                    {conversations.length > 0 && (
                                        <span className="task-detail-modal-conversations-count">
                                            ({conversations.length})
                                        </span>
                                    )}
                                </h4>
                                <button
                                    className="task-detail-modal-conversations-toggle"
                                    onClick={() => setShowConversations(!showConversations)}
                                >
                                    {showConversations ? 'Hide' : 'Show'}
                                </button>
                            </div>
                            {showConversations && (
                                <div className="task-detail-modal-conversations-content">
                                    {loadingConversations ? (
                                        <div className="task-detail-modal-conversations-loading">
                                            Loading conversations...
                                        </div>
                                    ) : conversations.length === 0 ? (
                                        <div className="task-detail-modal-conversations-empty">
                                            No conversations found for this task.
                                        </div>
                                    ) : (
                                        <div className="task-detail-modal-conversations-list">
                                            {conversations.map((conv, index) => (
                                                <div
                                                    key={conv.id || index}
                                                    className={`task-detail-modal-conversation-item ${selectedConversation?.id === conv.id ? 'active' : ''}`}
                                                    onClick={() => setSelectedConversation(conv)}
                                                >
                                                    <div className="task-detail-modal-conversation-item-header">
                                                        <span className="task-detail-modal-conversation-item-date">
                                                            {new Date(conv.workPlanDate).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            })}
                                                        </span>
                                                        {conv.user && (
                                                            <span className="task-detail-modal-conversation-item-user">
                                                                {conv.user.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="task-detail-modal-conversation-item-preview">
                                                        {conv.conversationMessages?.[0]?.content?.substring(0, 100) || 'No messages'}
                                                        {conv.conversationMessages?.[0]?.content?.length > 100 ? '...' : ''}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Selected Conversation Viewer */}
                    {userIsManager && selectedConversation && (
                        <div className="task-detail-modal-conversation-viewer-container">
                            <div className="task-detail-modal-conversation-viewer-header">
                                <h4>Conversation Details</h4>
                                <button
                                    className="task-detail-modal-conversation-viewer-close"
                                    onClick={() => setSelectedConversation(null)}
                                >
                                    <FeatherX size={18} />
                                </button>
                            </div>
                            <div className="task-detail-modal-conversation-viewer-wrapper">
                                <TaskConversationViewer
                                    conversation={selectedConversation.conversationMessages || []}
                                    taskTitle={selectedConversation.taskTitle || task.title}
                                    taskDescription={task.description}
                                    userName={selectedConversation.user?.name}
                                    workPlanDate={selectedConversation.workPlanDate}
                                    taskStatusAtTime={selectedConversation.taskStatusAtTime}
                                    project={selectedConversation.project}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Feature Tag Detail Modal */}
            {task?.feature_tag && (
                <FeatureTagDetailModal
                    tag={task.feature_tag}
                    isOpen={isFeatureTagModalOpen}
                    onClose={() => setIsFeatureTagModalOpen(false)}
                    tasks={task ? [task] : []}
                    users={[]}
                />
            )}
        </div>
    );
};

export default TaskDetailModal;

